import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupIcon from "@mui/icons-material/Group";
import { GET_PEOPLE_CHECKS } from "../../../apis/organization-apis/people-apis/peopleApis";
import "./PeopleFlow.css";
import { CheckStatus, CheckCategory } from "../../../utils/enums";

const OverView = ({ setValue, staffDetails, count }) => {
  const [staffOverview, setStaffOverview] = useState({});
  const { data: checksData, loading: checksLoading } = useQuery(
    GET_PEOPLE_CHECKS,
    {
      variables: {
        active_check_category: CheckCategory?.PEOPLE_CHECK,
      },
    }
  );

  const peopleChecks = checksData?.getPeopleChecks || [];

  const passingChecks = peopleChecks?.filter(
    (check) => check?.status === CheckStatus?.PASS
  );
  const failingChecks = peopleChecks?.filter(
    (check) => check?.status === CheckStatus?.FAIL
  );
  const naChecks = peopleChecks?.filter(
    (check) => check?.status === CheckStatus?.NA
  );
  const totalChecks = peopleChecks?.length;
  const totalPassing = passingChecks?.length;
  const totalFailing = failingChecks?.length;
  const totalNA = naChecks?.length;
  const percent =
    totalChecks > 0 ? Math.round((totalPassing / totalChecks) * 100) : 0;

  const activeStaff =
    staffDetails?.getEmployees?.data?.filter(
      (val) => val?.in_scope !== false && !val?.termination_date
    ) || [];

  const notInScopeStaff =
    staffDetails?.getEmployees?.data?.filter((val) => val?.in_scope === false) ||
    [];

  const offboardedStaff =
    staffDetails?.getEmployees?.data?.filter(
      (val) => val?.termination_date
    ) || [];

  if (checksLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1200px", margin: "0 auto", p: 3 }}>
      {/* Main Staff Card */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  {activeStaff.length}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  staff in total
                </Typography>
              </Box>
            </Box>
            <IconButton
              sx={{
                backgroundColor: "#e3f2fd",
                "&:hover": { backgroundColor: "#bbdefb" },
                color: "#1976d2",
              }}
              onClick={() => setValue("2")}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Staff breakdown */}
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={{ display: "flex", gap: 4, color: "text.secondary" }}>
              <Typography variant="body2">
                <Typography
                  component="span"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {activeStaff?.length}
                </Typography>{" "}
                in-scope
              </Typography>
              <Typography variant="body2">
                <Typography
                  component="span"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {notInScopeStaff?.length}
                </Typography>{" "}
                not-in-scope
              </Typography>
              <Typography variant="body2">
                <Typography
                  component="span"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {offboardedStaff?.length}
                </Typography>{" "}
                offboarded
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Roles & responsibilities Card */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            p: 3,
            cursor: "pointer",
            "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.15)" },
          }}
          onClick={() => setValue("4")}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                Roles & responsibilities
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.5 }}
              >
                Define and manage roles and responsibilities for staff members
                to ensure clear accountability and proper task allocation within
                your organization.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  3 roles
                </Typography>
              </Box>
            </Box>

            <IconButton
              sx={{
                backgroundColor: "#e3f2fd",
                "&:hover": { backgroundColor: "#bbdefb" },
                color: "#1976d2",
              }}
              onClick={() => setValue("2")}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Checks Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            maxWidth: 1200,
            mx: "auto",
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            p: 3,
          }}
        >
          {/* Checks Table Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                System Checks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalPassing} passing, {totalFailing} failing, {totalNA} N/A
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: percent === 100 ? "#4caf50" : "#ff9800",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              border: "1px solid #F0F0F0",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {peopleChecks.map((check) => (
              <Box
                key={check.active_check_id}
                sx={{
                  p: 2,
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "0.97rem" }}
                  >
                    {check?.active_check_description}
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    color: "#031B89",
                    textTransform: "none",
                    fontWeight: 500,
                    ml: 1,
                    minWidth: 0,
                    p: 0,
                    textDecoration: "underline",
                  }}
                  onClick={() =>
                    setValue(check?.monitor_type === "admin" ? "4" : "2")
                  }
                >
                  View & Fix
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OverView;
